import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Plus, Edit, Trash2, Eye, Calendar, Upload, Image as ImageIcon } from 'lucide-react';
import * as serviceService from '../services/serviceService';
import { Service } from '../types';

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{
    image?: File;
  }>({});
  const [imageRemoved, setImageRemoved] = useState(false);

  // Her dil için ayrı form state'leri
  const [formTitles, setFormTitles] = useState({ tr: '', en: '', es: '' });
  const [formDescriptions, setFormDescriptions] = useState({ tr: '', en: '', es: '' });

  // Hizmetleri API'den yükle
  const fetchServices = async () => {
    try {
      setLoading(true);
      const servicesData = await serviceService.getServices();
      console.log('Services received:', servicesData);
      setServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Hizmetler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Dil değiştiğinde content'i güncelle
  useEffect(() => {
    if (editingService) {
      // Yeni dile geç
      // Form state'lerini güncelle
    }
  }, [selectedLanguage, editingService]);

  const handleEdit = (service: Service) => {
    // Normalize service data
    const normalizedService = {
      ...service,
      id: service.id || service._id,
      title: service.title || { tr: '', en: '', es: '' },
      description: service.description || { tr: '', en: '', es: '' },
      status: service.status || 'draft'
    };
    setEditingService(normalizedService);
    
    // Form state'lerini ayrı ayrı ayarla
    setFormTitles({
      tr: normalizedService.title.tr || '',
      en: normalizedService.title.en || '',
      es: normalizedService.title.es || ''
    });
    setFormDescriptions({
      tr: normalizedService.description.tr || '',
      en: normalizedService.description.en || '',
      es: normalizedService.description.es || ''
    });
    
    // Mevcut görsel varsa önizleme olarak ayarla, yoksa temizle
    if (service.image && service.image !== 'string' && service.image !== null) {
      setImagePreview(service.image);
    } else {
      setImagePreview(null);
    }
    
    // Selected files'ı temizle
    setSelectedFiles({});
    
    // Silme durumlarını sıfırla
    setImageRemoved(false);
    
    setShowForm(true);
  };

  const handleNewService = () => {
    setEditingService(null);
    setImagePreview(null);
    setSelectedFiles({});
    
    // Form state'lerini temizle
    setFormTitles({ tr: '', en: '', es: '' });
    setFormDescriptions({ tr: '', en: '', es: '' });
    
    // Silme durumlarını sıfırla
    setImageRemoved(false);
    
    setShowForm(true);
  };

  // Görsel yükleme fonksiyonları
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Önceki blob URL'i temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setSelectedFiles(prev => ({ ...prev, image: file }));
      setImageRemoved(false); // Yeni dosya seçildiğinde silme durumunu sıfırla
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedFiles(prev => ({ ...prev, image: undefined }));
    setImageRemoved(true); // Görsel silindiğini işaretle
  };

  const handleImageUpload = async (serviceId: string) => {
    if (selectedFiles.image) {
      try {
        console.log("Uploading service image for ID:", serviceId);
        await serviceService.uploadServiceImage(serviceId, selectedFiles.image);
        console.log("Service image uploaded successfully");
        return true;
      } catch (error) {
        console.error("Error uploading service image:", error);
        toast.error('Görsel yüklenirken hata oluştu');
        return false;
      }
    }
    return true;
  };

  const handleDelete = async (service: Service) => {
    if (window.confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) {
      try {
        const serviceId = service.id || service._id;
        if (!serviceId) {
          toast.error('Hizmet ID bulunamadı');
          return;
        }
        await serviceService.deleteService(serviceId);
        toast.success('Hizmet başarıyla silindi');
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
        toast.error('Hizmet silinirken hata oluştu');
      }
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      const serviceData = {
        title: {
          tr: formData.get('title_tr') as string || '',
          en: formData.get('title_en') as string || '',
          es: formData.get('title_es') as string || '',
        },
        description: {
          tr: formData.get('description_tr') as string || '',
          en: formData.get('description_en') as string || '',
          es: formData.get('description_es') as string || '',
        },
        status: formData.get('status') as string || 'draft'
      };

      let serviceId;
      if (editingService) {
        const id = editingService.id || editingService._id;
        if (!id) {
          toast.error('Hizmet ID bulunamadı');
          return;
        }
        await serviceService.updateService(id, serviceData);
        serviceId = id;
        toast.success('Hizmet başarıyla güncellendi');
      } else {
        const result = await serviceService.createService(serviceData);
        serviceId = result._id || result.id;
        toast.success('Hizmet başarıyla oluşturuldu');
      }

      // Dosya silme işlemleri (önce silme, sonra yükleme)
      if (serviceId && editingService && imageRemoved) {
        try {
          await serviceService.removeServiceImage(serviceId);
          console.log('Service image removed successfully');
        } catch (error) {
          console.error('Error removing service image:', error);
          toast.error('Görsel silinirken hata oluştu');
        }
      }

      // Görsel yükleme
      if (serviceId && selectedFiles.image) {
        const uploadSuccess = await handleImageUpload(serviceId);
        if (!uploadSuccess) {
          toast.error('Hizmet kaydedildi ancak görsel yüklenemedi');
        }
      }

      // Blob URL'leri temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      setShowForm(false);
      setEditingService(null);
      setImagePreview(null);
      setSelectedFiles({});
      
      // Silme durumlarını sıfırla
      setImageRemoved(false);
      
      fetchServices();
    } catch (error: any) {
      console.error('Error saving service:', error);
      toast.error('Hizmet kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const handleCloseForm = () => {
    // Blob URL'leri temizle
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    
    setShowForm(false);
    setEditingService(null);
    setImagePreview(null);
    setSelectedFiles({});
    setImageRemoved(false);
  };

  if (loading) {
    return (
      <MainLayout title="Hizmetler">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Hizmetler">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hizmetler</h1>
            <p className="text-gray-600">Şirket hizmetlerini yönetin</p>
          </div>
          <button
            onClick={handleNewService}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Yeni Hizmet
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Hizmet</p>
                <p className="text-2xl font-bold text-gray-900">{services.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Yayınlanan</p>
                <p className="text-2xl font-bold text-gray-900">
                  {services.filter(service => service.status === 'published').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Edit className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Taslak</p>
                <p className="text-2xl font-bold text-gray-900">
                  {services.filter(service => service.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Hizmet Listesi</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Görsel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Başlık
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Açıklama
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {services.map((service) => (
                  <tr key={service.id || service._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {service.image && service.image !== 'string' && service.image !== null ? (
                          <img 
                            src={service.image} 
                            alt="Service" 
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {typeof service.title === 'string' ? service.title : (service.title?.tr || 'İsimsiz Hizmet')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {typeof service.description === 'string' ? service.description : (service.description?.tr?.substring(0, 100) || '')}
                        {((typeof service.description === 'string' ? service.description : service.description?.tr) || '').length > 100 && '...'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(service.status || 'draft')}>
                        {service.status === 'published' ? 'Yayınlandı' : 'Taslak'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {service.createdAt ? new Date(service.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(service)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(service)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet'}
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleSubmit(formData);
              }} className="p-6 space-y-6">
                
                {/* Language Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Düzenleme Dili
                  </label>
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    {[
                      { code: 'tr', name: 'Türkçe' },
                      { code: 'en', name: 'English' },
                      { code: 'es', name: 'Español' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code as 'tr' | 'en' | 'es')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          selectedLanguage === lang.code
                            ? 'bg-white text-primary-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık ({selectedLanguage.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    name={`title_${selectedLanguage}`}
                    value={formTitles[selectedLanguage] || ''}
                    onChange={(e) => setFormTitles(prev => ({
                      ...prev,
                      [selectedLanguage]: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Hizmet başlığını girin"
                    required
                  />
                  
                  {/* Hidden inputs for other languages */}
                  {['tr', 'en', 'es'].map(lang => (
                    lang !== selectedLanguage && (
                      <input
                        key={lang}
                        type="hidden"
                        name={`title_${lang}`}
                        value={formTitles[lang as 'tr' | 'en' | 'es'] || ''}
                      />
                    )
                  ))}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama ({selectedLanguage.toUpperCase()})
                  </label>
                  <textarea
                    rows={4}
                    name={`description_${selectedLanguage}`}
                    value={formDescriptions[selectedLanguage] || ''}
                    onChange={(e) => setFormDescriptions(prev => ({
                      ...prev,
                      [selectedLanguage]: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Hizmet açıklamasını girin"
                    required
                  />
                  
                  {/* Hidden inputs for other languages */}
                  {['tr', 'en', 'es'].map(lang => (
                    lang !== selectedLanguage && (
                      <textarea
                        key={lang}
                        name={`description_${lang}`}
                        value={formDescriptions[lang as 'tr' | 'en' | 'es'] || ''}
                        style={{ display: 'none' }}
                        readOnly
                      />
                    )
                  ))}
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hizmet Görseli
                  </label>
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Service preview"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => document.getElementById('service-image-input')?.click()}
                      className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Görsel yüklemek için tıklayın</p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF (Max 5MB)</p>
                    </div>
                  )}
                  
                  <input
                    id="service-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    name="status"
                    defaultValue={editingService?.status || 'draft'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="draft">Taslak</option>
                    <option value="published">Yayınlandı</option>
                  </select>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingService ? 'Güncelle' : 'Oluştur')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Services;
